import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SeoHead from '../../components/seo/SeoHead';
import {
  ArrowLeft,
  MapPin,
  Phone,
  Clock,
  Navigation,
  Heart,
  Pill,
  Sparkles,
  Baby,
  FlaskConical,
  Users,
  LineChart,
  ScanLine,
  Star,
  Plane,
} from 'lucide-react';

import { LOCAL_AREAS } from './localAreasData';

const PHONE_PRIMARY = '+225 07 78 41 52 77';
const PHONE_SECONDARY = '+225 27 22 30 05 72';
const GOOGLE_MAPS_URL =
  'https://www.google.com/maps/place/Pharmacie+Saint+Ange+Des+Amis/data=!4m2!3m1!1s0x0:0x590d810c976b53b7';

/** Shared "services" grid. Same services everywhere; quartier-specific content
 *  is injected via the hero / about / FAQ blocks. */
const SERVICES = [
  { icon: Pill, name: 'Médicaments', description: 'Large gamme princeps et génériques, approvisionnement quotidien, ruptures rares.' },
  { icon: Sparkles, name: 'Parapharmacie', description: 'Gammes françaises complètes : La Roche-Posay, Avène, Bioderma, Nuxe, Caudalie, Uriage, Sensibio…' },
  { icon: Baby, name: 'Laits infantiles', description: 'Formules standards et spécialisées (AR, HA, épaissies, prématurés) des marques Gallia, Guigoz, Modilac, Novalac, Nutriben.' },
  { icon: FlaskConical, name: 'Produits FIV', description: 'Conseil pharmaceutique dédié aux traitements de fertilité, confidentialité assurée.' },
  { icon: Plane, name: 'Commandes France & Suisse', description: 'Importation directe des produits introuvables en Côte d\'Ivoire, livraison sous ~10 jours.' },
  { icon: ScanLine, name: 'Conseil dermo par IA', description: "Application SADASkinCare : analyse de votre peau, recommandations personnalisées, gratuit." },
  { icon: LineChart, name: 'Suivi santé', description: 'Enregistrement tension, glycémie, IMC, SpO₂ avec rapport PDF aux normes OMS.' },
  { icon: Heart, name: 'Accueil chaleureux', description: 'Équipe jeune et dynamique formée en continu, espace de confidentialité premium.' },
];

/** Universal FAQ items appended to area-specific ones — ensures each page has
 *  a dense FAQ block for Google position-0 opportunities. */
const UNIVERSAL_FAQ = [
  {
    question: "Quelle est l'adresse exacte de la Pharmacie Saint Ange Des Amis ?",
    answer:
      "Carrefour Bluetooth, Angré 7ème Tranche, Cocody, Abidjan — dans l'enceinte de l'ancien Bar Bluetooth. Code Plus : 92V7+H8 Abidjan. Téléphones : 07 78 41 52 77 / 27 22 30 05 72.",
  },
  {
    question: 'Faites-vous partie des pharmacies de garde d\'Abidjan ?',
    answer:
      "Oui, nous participons régulièrement au tableau des pharmacies de garde d'Abidjan. Pendant notre semaine de garde, nous sommes ouverts 24h/24. Le tableau à jour est consultable sur notre site.",
  },
];

const Breadcrumb = ({ area }) => (
  <nav className="text-sm text-[#8B7355] mb-3">
    <a href="/" className="hover:text-[#C45C26] hover:underline">Accueil</a>
    <span className="mx-2">›</span>
    <a href="/pharmacie-cocody-angre" className="hover:text-[#C45C26] hover:underline">Cocody Angré</a>
    <span className="mx-2">›</span>
    <span className="text-[#3D2E1F] font-medium">{area.area_name}</span>
  </nav>
);

const PharmacyAreaSeoPage = ({ areaSlug, onBack }) => {
  const area = LOCAL_AREAS[areaSlug];

  if (!area) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#8B7355]">Page introuvable</p>
          <Button onClick={onBack} className="mt-4">Retour</Button>
        </div>
      </div>
    );
  }

  const canonicalPath = `/${area.slug}`;
  const fullFaq = [...area.faq, ...UNIVERSAL_FAQ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        mainEntity: fullFaq.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://phciesada.org/' },
          { '@type': 'ListItem', position: 2, name: 'Cocody Angré', item: 'https://phciesada.org/pharmacie-cocody-angre' },
          { '@type': 'ListItem', position: 3, name: area.area_name, item: `https://phciesada.org${canonicalPath}` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8F3] to-white">
      <SeoHead
        title={area.title}
        description={area.description}
        keywords={area.keywords.join(', ')}
        canonical={canonicalPath}
        structuredData={structuredData}
      />

      {/* Back button */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <Button variant="ghost" onClick={onBack} className="text-[#5D4E37]" data-testid="area-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 py-10 md:py-16">
        <Breadcrumb area={area} />
        <Badge className="bg-amber-100 text-amber-800 border-amber-300 mb-4">
          {area.badge_label}
        </Badge>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#3D2E1F] mb-4 leading-tight" data-testid="area-h1">
          Pharmacie <span className="text-[#C45C26]">{area.hero_h1_accent}</span>
        </h1>
        <p className="text-lg text-[#5D4E37] max-w-3xl leading-relaxed">
          {area.hero_intro}
        </p>

        {/* Distance strip */}
        <div className="flex flex-wrap gap-3 mt-6">
          <Badge className="bg-green-100 text-green-800 border-green-300">
            <Navigation className="w-3.5 h-3.5 mr-1" /> {area.distance_description}
          </Badge>
          <Badge className="bg-blue-100 text-blue-800 border-blue-300">
            <Star className="w-3.5 h-3.5 mr-1 fill-current" /> 5,0 sur Google (19 avis)
          </Badge>
          <Badge className="bg-purple-100 text-purple-800 border-purple-300">
            <Users className="w-3.5 h-3.5 mr-1" /> Équipe jeune & dynamique
          </Badge>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap gap-3 mt-8">
          <Button asChild size="lg" className="bg-[#C45C26] hover:bg-[#A64C1F] text-white">
            <a href={`tel:${PHONE_PRIMARY.replace(/\s/g, '')}`} data-testid="area-call-btn">
              <Phone className="w-4 h-4 mr-2" /> Appeler la pharmacie
            </a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-[#C45C26] text-[#C45C26]">
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="area-maps-btn">
              <Navigation className="w-4 h-4 mr-2" /> Itinéraire Google Maps
            </a>
          </Button>
        </div>
      </section>

      {/* About — audience-specific content block */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-amber-100">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3D2E1F] mb-4">
            Nos services pour les résidents de {area.area_name_long}
          </h2>
          <p className="text-[#5D4E37] leading-relaxed max-w-4xl">
            {area.audience_profile}
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-2xl md:text-3xl font-bold text-[#3D2E1F] mb-2">
          Une pharmacie complète, {SERVICES.length} services différenciants
        </h2>
        <p className="text-[#5D4E37] mb-8 max-w-3xl">
          Médicaments, parapharmacie, pédiatrie, fertilité, conseil dermo par IA, suivi santé personnalisé :
          chaque visite chez nous est une prise en charge globale.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i} className="border-amber-100 hover:shadow-lg transition-shadow">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-[#C45C26]/10 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[#C45C26]" />
                  </div>
                  <h3 className="font-semibold text-[#3D2E1F] mb-1">{s.name}</h3>
                  <p className="text-sm text-[#5D4E37] leading-relaxed">{s.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 py-10" id="faq">
        <h2 className="text-2xl md:text-3xl font-bold text-[#3D2E1F] mb-6">Questions fréquentes</h2>
        <div className="space-y-3">
          {fullFaq.map((item, i) => (
            <details
              key={i}
              className="bg-white rounded-xl border border-amber-100 overflow-hidden group"
            >
              <summary className="cursor-pointer p-4 font-semibold text-[#3D2E1F] flex items-center justify-between hover:bg-amber-50/50 transition-colors">
                <span>{item.question}</span>
                <span className="text-[#C45C26] group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <div className="px-4 pb-4 text-[#5D4E37] leading-relaxed border-t border-amber-100 pt-3">
                {item.answer}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Contact strip */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="bg-gradient-to-r from-[#C45C26] to-[#A64C1F] rounded-2xl p-6 md:p-10 text-white">
          <div className="flex flex-wrap justify-between items-center gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Passez nous voir aujourd'hui</h2>
              <p className="text-white/90 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Angré 7<sup>ème</sup> Tranche, Carrefour Bluetooth — Cocody, Abidjan
              </p>
              <p className="text-white/90 flex items-center gap-2 mt-1">
                <Clock className="w-4 h-4" />
                Lun–Ven 7h30 – 20h00 • Sam 7h30 – 13h00
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-white text-[#C45C26] hover:bg-amber-50">
                <a href={`tel:${PHONE_PRIMARY.replace(/\s/g, '')}`}>
                  <Phone className="w-4 h-4 mr-2" /> {PHONE_PRIMARY}
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href={`tel:${PHONE_SECONDARY.replace(/\s/g, '')}`}>
                  <Phone className="w-4 h-4 mr-2" /> {PHONE_SECONDARY}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-link to sibling areas — reinforces internal SEO mesh */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-xl md:text-2xl font-bold text-[#3D2E1F] mb-4">
          Vous habitez un autre quartier ? Nous desservons toute la zone
        </h2>
        <div className="flex flex-wrap gap-2">
          <a href="/pharmacie-cocody-angre" className="px-3 py-1.5 rounded-full bg-white border border-amber-200 text-sm text-[#5D4E37] hover:bg-amber-50 transition-colors">
            Pharmacie Cocody Angré
          </a>
          {Object.values(LOCAL_AREAS)
            .filter((a) => a.slug !== area.slug)
            .map((a) => (
              <a
                key={a.slug}
                href={`/${a.slug}`}
                className="px-3 py-1.5 rounded-full bg-white border border-amber-200 text-sm text-[#5D4E37] hover:bg-amber-50 transition-colors"
              >
                Pharmacie {a.area_name}
              </a>
            ))}
          <a href="/pharmacie-de-garde-abidjan" className="px-3 py-1.5 rounded-full bg-white border border-amber-200 text-sm text-[#5D4E37] hover:bg-amber-50 transition-colors">
            Pharmacie de garde Abidjan
          </a>
        </div>
      </section>
    </div>
  );
};

export default PharmacyAreaSeoPage;
